import React, { Fragment, FunctionComponent, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import styles from './DotsMap.module.scss';
import osm from '../../utils/osm-providers';
import 'leaflet/dist/leaflet.css';
import Marker1 from 'app/assets/images/marker1.png';
import Marker2 from 'app/assets/images/marker2.png';
import Marker3 from 'app/assets/images/marker3.png';
import Marker4 from 'app/assets/images/marker4.png';
import Marker5 from 'app/assets/images/marker5.png';
import * as L from 'leaflet';
import { useHistory } from 'react-router-dom';
import DotsMenu from '../DotsMenu';
import { ExpandIcon } from 'app/assets/images/icons';
import { _t } from 'app/utils/i18n';
import CardTitle from '../CardTitle';
import { checkTranslated } from 'app/utils/helpers';
import { navigateWithSearchState } from 'app/utils/search-state';

interface Props {
  data: Array<any>;
  showToolTip?: boolean;
  page?: string;
  expandUrl?: string;
  currentModule?: string;
  compId: string;
  hideDots?: boolean;
  MapHeight?: boolean;
  title?: string;
  additionalComponents?: JSX.Element;
  isReport?: boolean;
  expandAction?: any;
  hideLabels?: boolean;
  hideHashtag?: boolean;
}

type TContryChild = {
  country_name: string;
  id: number;
  image_url: string;
  lat: number;
  long: number;
  mentions: number;
  name: string | { original: string; arabic: string };
  percentage: number;
  rank?: string | number;
  additionalComponents?: JSX.Element;
};

const DotsMap: FunctionComponent<Props> = ({
  currentModule,
  data,
  showToolTip,
  page,
  expandUrl,
  compId,
  hideDots,
  MapHeight,
  title,
  additionalComponents,
  isReport,
  expandAction,
  hideLabels,
  hideHashtag,
}) => {
  // const [countries, setCountries] = useState(() =>
  //   page === 'compare' ? data.map((hashtag) => hashtag.countries) : data

  // );

  const history = useHistory();
  const countries =
    page === 'compare' && currentModule !== 'topics'
      ? data.map((hashtag) => hashtag.countries)
      : page === 'compare' && currentModule === 'topics'
        ? data.map((topic) =>
            topic?.countries?.filter(
              (country: TContryChild) =>
                country?.lat !== 0 &&
                country?.long !== 0 &&
                country?.country_name?.length > 0,
            ),
          )
        : data;
  const trendingBefore = countries?.filter((country) => isNaN(country?.rank));
  const trendingNow = countries?.filter((country) => !isNaN(country?.rank));

  const trendingNowBeforeData = [
    {
      name: 'trending now',
      countries: trendingNow,
    },
    {
      name: 'trending before',
      countries: trendingBefore,
    },
  ];

  const [center] = useState({ lat: 30, lng: 30 });
  const showExpand = expandAction || expandUrl;
  const RenderMap = useMemo(() => {
    if (countries?.length === 0) return '';
    if (countries?.some((item) => item?.name))
      return countries?.map((countryItem, index) => (
        <Marker
          position={[countryItem?.lat, countryItem?.long]}
          icon={markerIcon()}
          key={`${index}`}
          data-testid="countries-holder"
        >
          {showToolTip && (
            <Tooltip data-testid="tooltip-holder">
              {currentModule === 'topics' && page === 'compare'
                ? `${
                    typeof countryItem?.name === 'string'
                      ? countryItem?.name
                      : checkTranslated(
                          countryItem?.name?.original,
                          countryItem?.name?.arabic,
                        )
                  } / ${countryItem?.country_name}`
                : `${
                    typeof countryItem?.name === 'string'
                      ? countryItem?.name
                      : checkTranslated(
                          countryItem?.name.original,
                          countryItem?.name.arabic,
                        )
                  } ${!hideHashtag ? `#${countryItem?.rank}` : ''}`}
            </Tooltip>
          )}
        </Marker>
      ));

    return countries?.map((item, parentIndex) =>
      item?.map((countryItem: any, index: number) => (
        <Marker
          position={[countryItem?.lat, countryItem?.long]}
          icon={markerIcon(parentIndex)}
          key={`${index}`}
          data-testid="countries-holder"
        >
          {showToolTip && (
            <Tooltip data-testid="tooltip-holder">
              {currentModule === 'topics' && page === 'compare'
                ? `${
                    typeof countryItem?.name === 'string'
                      ? countryItem?.name
                      : checkTranslated(
                          countryItem?.name?.original,
                          countryItem?.name?.arabic,
                        )
                  }`
                : `${
                    typeof countryItem?.name === 'string'
                      ? countryItem?.name
                      : checkTranslated(
                          countryItem?.name?.original,
                          countryItem?.name?.arabic,
                        )
                  }${!hideHashtag ? `#${countryItem?.rank} ` : ''}`}
            </Tooltip>
          )}
        </Marker>
      )),
    );
  }, [countries]);
  return (
    <div id={compId}>
      <div id={`${compId}-dotsMap`} className="position-relative">
        {title && (
          <CardTitle title={title} compId={compId} hideIcon={isReport} />
        )}
        <div
          className={`${styles.DotsMap} ${MapHeight && styles.MapHeight}`}
          data-testid="map-holder"
        >
          <div className={styles.TopBar}>
            <div></div>
            <div className={styles.Controls}>
              {showExpand && !isReport && (
                <div
                  className={styles.ControlsItem}
                  data-testid="expand-holder"
                >
                  <div
                    className={styles.ActionBtn}
                    onClick={() =>
                      expandUrl
                        ? history.push(
                            navigateWithSearchState(expandUrl, {
                              currentModule: currentModule && currentModule,
                              data: data && data,
                              page: page && page,
                            }),
                          )
                        : expandAction?.()
                    }
                  >
                    <ExpandIcon />
                  </div>
                </div>
              )}
              {!hideDots && (
                <div className={styles.controlsItem}>
                  {!isReport && <DotsMenu id={`${compId}-dotsMap`} />}
                </div>
              )}
            </div>
          </div>
          <MapContainer
            center={center}
            zoom={2}
            scrollWheelZoom={false}
            zoomControl={!isReport}
          >
            <TileLayer
              url={osm?.maptiler?.url}
              attribution={osm?.maptiler?.attribution}
            />
            {RenderMap}
          </MapContainer>

          {!hideLabels && (
            <div
              className={
                page === 'compare'
                  ? styles.itemsContainer
                  : `${styles.itemsContainerInDetails} ${styles.itemsContainer}`
              }
              data-testid="items-container-holder"
            >
              {page === 'compare' ? (
                <>
                  {data.map((item, i) => (
                    <React.Fragment key={i}>
                      <div className={styles[`item${i + 1}`]} />
                      <p dir="auto">
                        {currentModule === 'topics'
                          ? `${checkTranslated(
                              item?.topic_name?.original,
                              item?.topic_name?.arabic,
                            )} (${
                              item?.countries?.filter(
                                (country: TContryChild) =>
                                  country?.lat !== 0 &&
                                  country?.long !== 0 &&
                                  country?.country_name.length > 0,
                              )?.length
                            })`
                          : `${checkTranslated(
                              item?.hashtag_name,
                              item?.hashtag_name_translated,
                            )}`}
                      </p>
                    </React.Fragment>
                  ))}
                  {/* Comment For Now  */}
                  {/* {currentModule === 'topics' ? (
                <>
                  <div className={styles.item6} />
                  <p> {_t('topics:never_interested')}</p>
                </>
              ) : (
                <>
                  <div className={styles.item6} />
                  <p>{_t('trends:never_trending')}</p>
                </>
              )} */}
                </>
              ) : (
                <>
                  {trendingNowBeforeData?.map(({ countries }, index) =>
                    countries?.length && countries?.[0]?.name?.length ? (
                      <Fragment key={index}>
                        <React.Fragment key={index}>
                          <div className={styles[`item${1}`]} />
                          <p dir="auto">
                            {`${
                              countries?.filter(
                                (country: TContryChild) =>
                                  country?.lat !== 0 &&
                                  country?.long !== 0 &&
                                  country?.country_name?.length > 0,
                              )?.length || countries?.length
                            }
                    ${
                      currentModule == 'topics'
                        ? _t('topics:intereset')
                        : _t(`basic:trending${index ? '_before' : ''}`)
                    }
                    
                    `}
                          </p>
                        </React.Fragment>
                      </Fragment>
                    ) : null,
                  )}
                  <div className={styles.item6} />
                  {currentModule == 'topics' ? (
                    <p>{_t('topics:never_interest')}</p>
                  ) : (
                    <p> {_t('trends:never_trending')}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {additionalComponents}
      </div>
    </div>
  );
};

export default DotsMap;

const markerMap = [Marker1, Marker2, Marker3, Marker4, Marker5];
const markerIcon = (parentIndex = 0) => {
  const icon = new L.Icon({
    iconUrl: markerMap[parentIndex],
    iconSize: [20, 20],
    iconAnchor: [10, 15],
    popupAnchor: [-3, -76],
    tooltipAnchor: [0, -46],
  });
  return icon;
};
